import { Modal } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SucessModal from 'src/components/sucess-modal';
import { paths } from 'src/paths';
import {
  getAssignedFacilities,
  getTherapistProfileAction,
  storeFacilityUuid,
} from 'src/redux/actions/facilityActions';
import loader from 'src/components/LoaderImg/loader.gif';
import AuthContext, { logoutUser } from 'src/routes/AuthContext';
import { setFacilityId } from 'src/hooks/use-facility-id';

/** One entry of the assigned-facilities list, as the facility dropdown reads it. */
interface AssignedFacility {
  uuid: string;
}

/**
 * Progress of the APIT-683 fallback: has the missing-last-facility lookup been started, and is it
 * still in flight? `IDLE` covers both "never needed" and "finished".
 */
const FacilityFallback = {
  IDLE: 'idle',
  RESOLVING: 'resolving',
} as const;

type FacilityFallbackState = (typeof FacilityFallback)[keyof typeof FacilityFallback];

function LandingPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { redirectSupervisor } = paths;
  const auth = useContext(AuthContext);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [facilityFallback, setFacilityFallback] = useState<FacilityFallbackState>(
    FacilityFallback.IDLE
  );
  const profileResponse = useSelector((state: any) => state?.getTherapistProfileReducer);
  const assignedFacilities = useSelector((state: any) => state?.getFacilityDropDownReducer);
  const __accessToken = auth.token;

  /**
   * Adopt `facilityUuid` as the session's facility and load the profile/permissions scoped to it.
   * Every request from here on carries it as the `Facility_id` header (see services/interceptor).
   */
  const bootstrapWithFacility = useCallback(
    (facilityUuid: string) => {
      setFacilityId(facilityUuid);
      dispatch(storeFacilityUuid(facilityUuid));
      dispatch(getAssignedFacilities());
      dispatch(getTherapistProfileAction());
    },
    [dispatch]
  );

  useEffect(() => {
    if (!__accessToken) return;
    const parsedToken: any = auth.tokenParsed || '';
    localStorage.setItem('realm', auth.realm || '');
    if (auth.tokenParsed?.realm_access?.roles?.includes('physician')) {
      dispatch(getTherapistProfileAction());
      return;
    }
    if (!parsedToken.lastLoggedInFacility) {
      // APIT-683: a brand-new therapist has no `lastLoggedInFacility` claim yet, so there is no
      // facility context to bootstrap with. This used to dead-end on the error modal, which
      // self-closes into a logout — the user could never finish logging in. Instead, ask for the
      // therapist's assigned facilities and fall back to the first one below.
      setFacilityFallback(FacilityFallback.RESOLVING);
      dispatch(getAssignedFacilities());
      return;
    }
    bootstrapWithFacility(parsedToken.lastLoggedInFacility);
  }, [__accessToken]);

  useEffect(() => {
    if (facilityFallback !== FacilityFallback.RESOLVING) return;
    // Still in flight: `isGetFacilities` only flips on success, and `response` is populated on
    // either outcome, so both falsy means nothing has come back yet.
    if (!assignedFacilities?.isGetFacilities && !assignedFacilities?.response) return;

    const facilityList = assignedFacilities?.response?.data?.data;
    const facilities: AssignedFacility[] = Array.isArray(facilityList) ? facilityList : [];
    const firstFacility = facilities[0];

    setFacilityFallback(FacilityFallback.IDLE);

    if (!assignedFacilities?.isGetFacilities || !firstFacility?.uuid) {
      // Either the lookup failed or the therapist really is assigned to no facility. Surface the
      // error rather than leaving the loader spinning — the modal is the only exit.
      setShowWarningModal(true);
      return;
    }

    // Zero index, per the ticket. `bootstrapWithFacility` re-requests the facility list, this time
    // with the `Facility_id` header set, so the dropdown and its follow-up calls (Paycom details,
    // clock in/out visibility) resolve against a real facility instead of an empty header.
    bootstrapWithFacility(firstFacility.uuid);
  }, [assignedFacilities, facilityFallback, bootstrapWithFacility]);

  useEffect(() => {
    if (profileResponse.response?.code === 'INTERNAL_ERROR') {
      navigate('/server-error');
      return;
    }
    if (profileResponse.response === null && !profileResponse.getAdminProfile) return;
    navigate(redirectSupervisor);
  }, [profileResponse]);

  const redirectToLoginPage = () => {
    logoutUser();
  };

  return (
    <div>
      {(profileResponse?.loader || facilityFallback === FacilityFallback.RESOLVING) && (
        <img
          src={loader}
          alt="loading..."
          style={{ margin: 'auto', display: 'block' }}
        />
      )}
      <Modal open={showWarningModal}>
        <SucessModal
          title="Error"
          message="You are not associated to any facility or Login details are incorrect, please try again"
          close={redirectToLoginPage}
          isSucess={false}
        />
      </Modal>
    </div>
  );
}

export default LandingPage;
